import { forwardRef, useLayoutEffect, useRef, useState } from "react";

// Raw "152350.75" → display "152,350.75" (keeps a trailing "." and up to 2 decimals while typing).
export const formatMoney = (raw) => {
    const s = String(raw ?? "");
    if (s === "") return "";
    const neg = s.startsWith("-");
    const [i, d] = s.replace(/[^0-9.]/g, "").split(".");
    const int = (i || "").replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return (neg ? "-" : "") + (int || (d !== undefined ? "0" : "")) + (d !== undefined ? "." + d.slice(0, 2) : "");
};

const clean = (text) => {
    let v = text.replace(/[^0-9.]/g, "");
    const dot = v.indexOf(".");
    if (dot !== -1) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "").slice(0, 2);
    return v;
};

// Money box with a fixed ₦ sign and live thousands separators. onChange receives { target: { value: "152350.75" } }.
export const MoneyInput = forwardRef(function MoneyInput({ value, onChange, className = "", wrapperClassName = "w-full", style, symbol = true, ...rest }, ref) {
    const inner = useRef(null);
    const caret = useRef(null);
    const [, force] = useState(0);
    const shown = formatMoney(value);

    useLayoutEffect(() => {
        const el = inner.current;
        if (!el || caret.current == null || document.activeElement !== el) return;
        // Put the caret back after the same number of digits/dots it followed before re-formatting.
        let seen = 0, pos = 0;
        while (pos < el.value.length && seen < caret.current) {
            if (/[0-9.]/.test(el.value[pos])) seen += 1;
            pos += 1;
        }
        el.setSelectionRange(pos, pos);
        caret.current = null;
    });

    const handle = (e) => {
        const el = e.target;
        caret.current = el.value.slice(0, el.selectionStart ?? el.value.length).replace(/[^0-9.]/g, "").length;
        const v = clean(el.value);
        onChange?.({ target: { value: v } });
        force((n) => n + 1);
    };

    const setRefs = (el) => {
        inner.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
    };

    return (
        <div className={`relative ${wrapperClassName}`}>
            {symbol && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink/45">₦</span>}
            <input
                {...rest}
                ref={setRefs}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={shown}
                onChange={handle}
                className={`${className} tabular-nums`}
                style={{ ...style, ...(symbol ? { paddingLeft: "1.75rem" } : {}) }}
            />
        </div>
    );
});

export default MoneyInput;
